import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Briefcase, Check, Phone } from 'lucide-react';
import { ensureTherapistWelcomeTrial } from '../services/welcomeTrial';
import { ensureInfluencerSignupBonus } from '../services/influencerSignupBonus';
import { formatBrazilPhone, isValidBrazilPhone } from '../utils/phoneFormat';
import Swal from 'sweetalert2';

type SelectableRole = 'paciente' | 'terapeuta';

const CompleteProfile: React.FC = () => {
  const { user, isLoading: authLoading, refreshAuthUser } = useAuth();
  const [selectedRole, setSelectedRole] = useState<SelectableRole>('paciente');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!user) return;

    if (!isValidBrazilPhone(phone)) {
      setError('Informe um telefone válido com DDD, ex.: (11) 94729-3221.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formattedPhone = formatBrazilPhone(phone);
      const userDocRef = doc(db, 'users', user.uid);

      await updateDoc(userDocRef, {
        role: selectedRole,
        phone: formattedPhone,
      });

      if (selectedRole === 'terapeuta') {
        const therapistRef = doc(db, 'therapists', user.uid);
        await setDoc(
          therapistRef,
          {
            userId: user.uid,
            nomePublico: user.name || 'Terapeuta',
            email: user.email,
            phone: formattedPhone,
            horasDisponiveis: 0,
            horariosDisponiveis: {},
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
        await ensureTherapistWelcomeTrial();
        await ensureInfluencerSignupBonus();
      }

      await refreshAuthUser();

      const tutorialNotice = await Swal.fire({
        icon: 'success',
        title: 'Cadastro concluído!',
        html:
          selectedRole === 'terapeuta'
            ? 'Enviamos para o seu e-mail o guia de acesso à sala virtual — confira sua caixa de entrada (e também o spam).<br/><br/>Você também pode assistir aos tutoriais quando quiser aqui no site, em <strong>Tutoriais</strong>.'
            : 'Você pode assistir aos tutoriais da plataforma quando quiser aqui no site, em <strong>Tutoriais</strong>.',
        showCancelButton: true,
        confirmButtonText: 'Ver tutoriais',
        cancelButtonText: 'Continuar',
        confirmButtonColor: '#2B5A41',
        cancelButtonColor: '#9CA3AF',
        allowOutsideClick: false,
      });

      if (tutorialNotice.isConfirmed) {
        navigate('/tutoriais');
        return;
      }

      const from = location.state?.from as { pathname?: string; search?: string } | undefined;
      if (from?.pathname) {
        navigate(`${from.pathname}${from.search || ''}`, { replace: true });
        return;
      }

      navigate('/painel');
    } catch (err) {
      console.error('Erro ao atualizar perfil:', err);
      setError('Não foi possível concluir o cadastro. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-8 sm:p-12 text-center">
        <img src="/assets/logo.png" alt="Logo" className="h-20 w-20 mx-auto mb-6" />

        <h1 className="text-3xl font-bold text-gray-800">Quase lá, {user.name?.split(' ')[0]}!</h1>
        <p className="text-gray-600 mt-2 mb-6">
          Para finalizar seu cadastro, informe seu telefone e como você usará a plataforma.
        </p>

        <div className="mb-8 text-left">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-600 mb-2">
            Telefone (WhatsApp)
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <Phone className="h-5 w-5" />
            </span>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatBrazilPhone(e.target.value))}
              placeholder="(11) 94729-3221"
              required
              className="w-full pl-10 pr-4 py-3 bg-[#E8E8E8] rounded-full border-2 border-transparent focus:border-teal-500 focus:bg-white outline-none transition text-gray-700"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <button
            type="button"
            onClick={() => setSelectedRole('paciente')}
            className={`relative p-6 border-2 rounded-xl text-left transition-all duration-200 ${
              selectedRole === 'paciente'
                ? 'border-teal-500 bg-teal-50 scale-105 shadow-lg'
                : 'border-gray-300 hover:border-teal-400'
            }`}
          >
            <User className="h-8 w-8 text-teal-600 mb-3" />
            <h3 className="font-bold text-lg text-gray-800">Sou Paciente</h3>
            <p className="text-sm text-gray-600">Quero participar de sessões e constelar meus temas.</p>
            {selectedRole === 'paciente' && (
              <div className="absolute top-3 right-3 bg-teal-500 text-white rounded-full p-1">
                <Check size={16} />
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => setSelectedRole('terapeuta')}
            className={`relative p-6 border-2 rounded-xl text-left transition-all duration-200 ${
              selectedRole === 'terapeuta'
                ? 'border-purple-500 bg-purple-50 scale-105 shadow-lg'
                : 'border-gray-300 hover:border-purple-400'
            }`}
          >
            <Briefcase className="h-8 w-8 text-purple-600 mb-3" />
            <h3 className="font-bold text-lg text-gray-800">Sou Terapeuta</h3>
            <p className="text-sm text-gray-600">Quero conduzir sessões e atender meus clientes.</p>
            {selectedRole === 'terapeuta' && (
              <div className="absolute top-3 right-3 bg-purple-500 text-white rounded-full p-1">
                <Check size={16} />
              </div>
            )}
          </button>
        </div>

        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full max-w-xs mx-auto bg-teal-600 text-white font-bold py-3 px-4 rounded-full hover:bg-teal-700 disabled:bg-teal-400 transition-all transform hover:scale-105 flex items-center justify-center"
        >
          {isLoading && (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          )}
          Confirmar e Continuar
        </button>
      </div>
    </div>
  );
};

export default CompleteProfile;
